/**
 * テキスト入力のバイト長制限に関するユーティリティ関数
 */

import { LANG_EN, LANG_JP } from "@/constants";
import i18n from "@/lib/i18n";

/**
 * 文字列のバイト長を計算する
 * @param str 計算対象の文字列
 * @returns バイト長
 */
export const getByteLength = (str: string): number => {
  if (!str) return 0;
  return new TextEncoder().encode(str).length;
};

/**
 * 文字列を指定したバイト長に制限する
 * @param value 制限対象の文字列
 * @param maxBytes 最大バイト長
 * @returns 制限されたバイト長の文字列
 */
export const limitByteLength = (value: string, maxBytes: number): string => {
  if (!value) return "";

  // 現在のバイト長を取得
  const byteLength = getByteLength(value);

  // バイト長が制限内なら元の値を返す
  if (byteLength <= maxBytes) return value;

  // バイト長が制限を超える場合は、制限内に収まる最大の文字列を見つける
  let result = "";
  let currentBytes = 0;

  // 1文字ずつ追加して、バイト長をチェック
  for (let i = 0; i < value.length; i++) {
    const char = value.charAt(i);
    const charBytes = getByteLength(char);

    if (currentBytes + charBytes <= maxBytes) {
      result += char;
      currentBytes += charBytes;
    } else {
      break;
    }
  }

  return result;
};

/**
 * 文字列の文字数を計算する
 * @param str 計算対象の文字列
 * @returns 文字数
 */
export const getCharCount = (str: string): number => {
  if (!str) return 0;
  return str.length;
};

/**
 * 文字列を指定した文字数に制限する
 * @param value 制限対象の文字列
 * @param maxChars 最大文字数
 * @returns 制限された文字数の文字列
 */
export const limitCharCount = (value: string, maxChars: number): string => {
  if (!value) return "";

  // 文字数が制限内なら元の値を返す
  if (value.length <= maxChars) return value;

  // 文字数が制限を超える場合は、制限内に収まるように切り詰める
  return value.substring(0, maxChars);
};

/**
 * バイト長制限付きの入力ハンドラーを作成する
 * @param onChange 元の変更ハンドラー
 * @param maxBytes 最大バイト長
 * @returns 制限付きの入力ハンドラー
 */
export const createByteLimitedChangeHandler = (
  onChange: (value: string) => void,
  maxBytes: number,
) => {
  return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const limitedValue = limitByteLength(e.target.value, maxBytes);

    // 元の値と異なる場合のみ更新（カーソル位置の問題を防ぐため）
    if (limitedValue !== e.target.value) {
      e.target.value = limitedValue;
    }

    onChange(limitedValue);
  };
};

/**
 * 文字数制限付きの入力ハンドラーを作成する
 * @param onChange 元の変更ハンドラー
 * @param maxChars 最大文字数
 * @returns 制限付きの入力ハンドラー
 */
export const createCharLimitedChangeHandler = (
  onChange: (value: string) => void,
  maxChars: number,
) => {
  return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const limitedValue = limitCharCount(e.target.value, maxChars);

    // 元の値と異なる場合のみ更新（カーソル位置の問題を防ぐため）
    if (limitedValue !== e.target.value) {
      e.target.value = limitedValue;
    }

    onChange(limitedValue);
  };
};

/**
 * 言語コードから表示用のラベルを取得する
 * @param language 言語コード
 * @returns 表示用の言語ラベル
 */
export const getLanguageLabel = (language: string): string => {
  switch (language) {
    case LANG_JP:
      return i18n.t("common.japanese");
    case LANG_EN:
      return i18n.t("common.english");
    default:
      return language;
  }
};
